import { Component, HostListener, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { EventOriginEnum, NavigateToEnum } from '@app/core/enums/analytics/analytics-value.enum';
import { EntityTypeEnum } from '@app/core/enums/entity-type.enum';
import { Project } from '@app/core/model/entities/project/project';
import { ManualWorkflowViewEnum } from '@app/core/model/entities/workflow/workflow';
import { FieldValidator } from '@app/core/model/other/field-validator';
import { DocumentModalService } from '@app/features/main/views/organization-documents/modals/document-modal.service';
import { BaseProjectSheet } from '@app/features/main/views/projects/project-sheet/base-project-sheet';
import { ProjectModalService } from '@app/features/main/views/projects/projects-inventory/modals/project-modal-service';
import { FormStateService } from '@app/shared/components/form-builder/form-state.service';
import { SingleEditService } from '@app/shared/services/single-edit-service';
import { FieldService } from '@services/field.service';
import { of } from 'rxjs';
import { filter, startWith, switchMap, tap } from 'rxjs/operators';

@Component({
    selector: 'projects-sidebar',
    templateUrl: './projects-sidebar.component.html',
  providers: [ProjectModalService, DocumentModalService, FormStateService],
    standalone: false
})
export class ProjectsSidebarComponent extends BaseProjectSheet implements OnInit {
  private readonly fieldService = inject(FieldService);
  private readonly singleEditService = inject(SingleEditService);

  public formId: string = 'project_sidebar';

  protected readonly EntityType = EntityTypeEnum;
  protected readonly ManualWorkflowViewEnum = ManualWorkflowViewEnum;
  public eventsOrigin = EventOriginEnum.SIDEPANEL;
  public preconditionForDeletion = false;

  public validators: FieldValidator[] = [];

  /**
   * Listen for sidebar toggle actions and updates from the FormStateService.
   * Update sidebar data and call Project service when necessary.
   */
  public ngOnInit(): void {
    super.ngOnInit();

    // Update the side panel's content whenever a new Project is selected
    this.projectsService.sidePanelToggle$
      .pipe(
        switchMap(projectData => {
          if (projectData) {
            return this.fieldService.getField('name', EntityTypeEnum.PROJECT)
              .pipe(
                switchMap(field => {
                  this.validators = field.validators;
                  return this.projectsService.projectUpdated$;
                }),
                filter(updatedProject => updatedProject.id === projectData.id),
                startWith(projectData)
              );
          } else {
            return of(projectData);
          }
        }),
        tap(() => {
          this.singleEditService.singleEditSubject.next(null);

          // Unload current Project
          if (this.appManager.currentEntity instanceof Project) {
            this.appManager.unloadCurrentEntity();
          }
        }),
        filter(project => !!project),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(project => {
        this.project = project;
        this.appManager.currentEntity = project;
        this.loadProjectData();

        // Check if the user has permission to delete the project.
        // Only super admin, group with all asset and type access or with access to all the projects' related assets has permission to delete.
        const currentUserGroup = this.accessManager.currentUser.groups?.firstOrNull(group => group.client.organizationId === this.appManager.currentOrganization.id);
        if (this.accessManager.isSuperAdmin() || (currentUserGroup?.allAssetAccess && currentUserGroup?.allAssetTypeAccess)) {
          this.preconditionForDeletion = true;
        } else {
          const projectAssetIds = this.project.assetIds;
          const projectAssetTypeIds = (currentUserGroup.allAssetAccess || currentUserGroup.assetIds.some(assetId => projectAssetIds.includes(assetId)))
            && (currentUserGroup.allAssetTypeAccess || currentUserGroup.assetTypeIds.some(assetTypeId => projectAssetTypeIds.includes(assetTypeId)));
        }
      });
  }

  /**
   * Open a dialog to delete the current Project entity if the user confirms.
   */
  public deleteProject(): void {
    this.analyticsService.trackNavigationEvent(
      EventOriginEnum.SIDEPANEL,
      NavigateToEnum.DELETE_DIALOG,
      EntityTypeEnum.PROJECT,
      this.project.id
    );
    this.projectModalService.openDeleteProjectsDialog([this.project])
      .pipe(
        filter(result => !!result),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => this.snackbarManager.showActionSnackbar(this.translate.instant('SUCCESS.PROJECT_DELETED')));
  }

  /**
   * Close the sidebar.
   */
  public closeProjectSidePanel(): void {
    this.projectsService.closeProjectSidePanel();
  }

  /**
   * Bypass Material escape handler to call closeProjectSidebar,
   * which manage the current entity stacks.
   * @param event
   */
  @HostListener('keyup', ['$event'])
  public handleKeyboardEvent(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      this.closeProjectSidePanel();
    }
  }

  /**
   * Navigate to the Project's sheet.
   */
  public async navigateToProjectSheet(): Promise<void> {
    this.analyticsService.trackNavigationEvent(
      EventOriginEnum.SIDEPANEL,
      NavigateToEnum.SHEET,
      EntityTypeEnum.PROJECT,
      this.project.id
    );
    await this.projectsService.navigateToProjectSheet(this.project.id);
  }

  /**
   * Open a dialog to upload Documents and link updated Documents to the Project entity.
   */
  public uploadProjectDocuments(): void {
    this.analyticsService.trackNavigationEvent(
      EventOriginEnum.SIDEPANEL,
      NavigateToEnum.UPLOAD_DIALOG,
      EntityTypeEnum.PROJECT,
      this.project.id
    );
    super.uploadProjectDocuments();
  }
}
